import React, { useEffect, useMemo, useRef, useState } from "react";
import { demoMatchProgress } from "../../shared/progress";
import { wormColors, wormColorIds } from "core/src/core/constants.js";
import { flagUrlSquare } from "../../shared/flags";
import RoyaleLayer from "../play/dynamic/RoyaleLayer";
import RoyaleReward from "../play/dynamic/RoyaleReward";
import type { RoyaleMe } from "../../shared/royale";
import type { DynamicLayerProps, PublicPlayer } from "../play/dynamic/types";
import type { NextRound, RoundResult, RoundSetup, RoyaleResult } from "../../online/dynamic";

// /debug/royale-layer: the Royale layer over a plain dark board with mock data.
// The strip at the bottom jumps between every state; entering a state (or
// Replay) restarts its timeline. ?s=<n> opens on state n, ?t=<ms> starts that
// many ms into its pause.

const NUM = 215;
const SIZE = 30;
const NAMES = ["Mathias", "Kurvenkönig", "Lina", "Tobbe", "Sakura", "Diego", "Achtung99", "Mika", "Zoë", "Brick", "Ola", "Curvy", "Nils", "Petra", "Kenji", "Lucía", "Bo", "Freja", "Hamid", "Ines", "Jock", "Kaya", "Leon", "Maja", "Noor", "Otto", "Pia", "Quinn", "Rasmus", "Siv"];
const FLAGS = ["se", "de", "fi", "no", "jp", "es", "us", "fi", "nl", "gb", "no", "ca", "dk", "pl", "jp", "mx", "se", "dk", "ir", "pt", "gb-sct", "tr", "at", "hr", "ma", "ch", "ee", "ie", "is", "se"];
const ALL: PublicPlayer[] = NAMES.map((name, i) => ({ id: i === 0 ? "me" : `p${i}`, name, flag: FLAGS[i], colorId: wormColorIds[i % wormColorIds.length] }));
const BY_ID: Record<string, PublicPlayer> = Object.fromEntries(ALL.map((p) => [p.id, p]));
const ME = ALL[0], RIVAL = ALL[1];

const setupOf = (o: Partial<RoundSetup>): RoundSetup => ({
  dynamic: "royale", mode: "Classic", mapDesc: null, sizeKey: "huge", shape: "Stadium", round: 1, scoreReset: false,
  number: NUM, stage: "huge", alive: 30, out: 3, final: false, finalWins: 3, size: SIZE, royaleRound: 1, ...o,
});
const FINAL_SETUP = setupOf({ sizeKey: "small", stage: "final", alive: 2, out: 0, final: true, round: 16, royaleRound: 15, mode: "Fatboy Slim" });

interface Step {
  name: string;
  phase: DynamicLayerProps["phase"];
  setup: RoundSetup;
  next?: NextRound;
  result?: RoundResult;
  royaleResult?: RoyaleResult;
  players: PublicPlayer[];
  score?: Record<string, number>;
  roundWinnerId?: string;
  seatIds?: string[];
  youIds?: string[];
  myPlace?: number;
  pauseMs?: number;
  skipMs?: number;        // start this far into the pause (straight to its middle part)
  crashes?: [number, string][]; // [ms, player id]: who crashes while the state plays
  reward?: RoyaleMe | "guest";    // the winner's reward under the card (RoyaleReward), as it would read
}
const FIRST_WIN: RoyaleMe = { wins: 1, played: 6, bestPlace: 1, opened: [], openable: ["crown_1"] };
const FIFTH_WIN: RoyaleMe = { wins: 5, played: 31, bestPlace: 1, opened: ["crown_1"], openable: ["crown_5"] };
const THIRD_WIN: RoyaleMe = { wins: 3, played: 22, bestPlace: 1, opened: ["crown_1"], openable: [] };
const others = (n: number) => ALL.slice(1, n + 1);
const STEPS: Step[] = [
  { name: "Round 1", phase: "playing", setup: setupOf({}), players: ALL, crashes: [[1500, "p5"], [3400, "p12"], [5500, "p18"]] },
  {
    name: "Round over", phase: "countdown", setup: setupOf({}), players: ALL, pauseMs: 6500,
    result: { royale: true, out: ["p5", "p12", "p18"], places: { p5: 30, p12: 29, p18: 28 }, alive: 27, winner: null },
    next: { ...setupOf({ round: 2, royaleRound: 2, alive: 27, mode: "Sabotage", shape: "Rectangle" }), stageChanged: false },
  },
  {
    name: "I am out", phase: "countdown", setup: setupOf({ round: 2, royaleRound: 2, alive: 27 }), players: ALL.slice(0, 27), pauseMs: 6500,
    result: { royale: true, out: ["p7", "me", "p20"], places: { p7: 27, me: 26, p20: 25 }, alive: 24, winner: null },
    next: { ...setupOf({ round: 3, royaleRound: 3, alive: 24, mode: "Drink and drive" }), stageChanged: false }, myPlace: 26,
  },
  {
    name: "Watching", phase: "playing", setup: setupOf({ round: 3, royaleRound: 3, alive: 24 }), players: others(24), youIds: [], myPlace: 26,
    crashes: [[1800, "p3"]],
  },
  {
    name: "Map shrinks", phase: "countdown", setup: setupOf({ round: 5, royaleRound: 5, alive: 18 }), players: ALL.slice(0, 18), pauseMs: 9000, skipMs: 3000,
    result: { royale: true, out: ["p9", "p14", "p16"], places: { p9: 18, p14: 17, p16: 16 }, alive: 15, winner: null },
    next: { ...setupOf({ round: 6, royaleRound: 6, alive: 15, out: 2, sizeKey: "large", stage: "large", mode: "Identity crisis" }), stageChanged: true },
  },
  {
    name: "Final intro", phase: "countdown", setup: setupOf({ round: 14, royaleRound: 14, alive: 3, out: 1, sizeKey: "small", stage: "small" }), players: ALL.slice(0, 3), pauseMs: 11000, skipMs: 3000,
    result: { royale: true, out: ["p2"], places: { p2: 3 }, alive: 2, winner: null },
    next: { ...FINAL_SETUP, round: 15, scoreReset: true, stageChanged: true },
  },
  { name: "Final round", phase: "playing", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 1, p1: 1 } },
  {
    name: "Final score", phase: "countdown", setup: { ...FINAL_SETUP, round: 17 }, players: [ME, RIVAL], pauseMs: 6500,
    score: { me: 2, p1: 1 }, roundWinnerId: "me", result: { royale: true, out: [], alive: 2, winner: null },
    next: { ...FINAL_SETUP, round: 18, mode: "Sabotage", shape: "Rectangle" },
  },
  {
    name: "Winner: me (first win)", reward: FIRST_WIN, phase: "over", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 3, p1: 1 }, myPlace: 1,
    result: { royale: true, out: [], alive: 2, winner: "me" }, royaleResult: { number: NUM, winner: "me", placements: ["me", "p1"], size: SIZE },
  },
  {
    name: "Winner: 5th win", reward: FIFTH_WIN, phase: "over", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 3, p1: 2 }, myPlace: 1,
    result: { royale: true, out: [], alive: 2, winner: "me" }, royaleResult: { number: NUM, winner: "me", placements: ["me", "p1"], size: SIZE },
  },
  {
    name: "Winner: 3rd win", reward: THIRD_WIN, phase: "over", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 3, p1: 0 }, myPlace: 1,
    result: { royale: true, out: [], alive: 2, winner: "me" }, royaleResult: { number: NUM, winner: "me", placements: ["me", "p1"], size: SIZE },
  },
  {
    name: "Winner: guest", reward: "guest", phase: "over", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 3, p1: 1 }, myPlace: 1,
    result: { royale: true, out: [], alive: 2, winner: "me" }, royaleResult: { number: NUM, winner: "me", placements: ["me", "p1"], size: SIZE },
  },
  {
    name: "2nd place", phase: "over", setup: FINAL_SETUP, players: [ME, RIVAL], score: { me: 2, p1: 3 }, myPlace: 2,
    result: { royale: true, out: [], alive: 2, winner: "p1" }, royaleResult: { number: NUM, winner: "p1", placements: ["p1", "me"], size: SIZE },
  },
  {
    name: "Spectator", phase: "over", setup: FINAL_SETUP, players: [RIVAL, ALL[2]], seatIds: [], youIds: [],
    result: { royale: true, out: [], alive: 2, winner: "p1" }, royaleResult: { number: NUM, winner: "p1", placements: ["p1", "p2"], size: SIZE },
  },
];

const css = `
.rll { position: fixed; inset: 0; background: #060b18; color: #e9f3ff; font-family: "Outfit", system-ui, sans-serif; overflow: hidden; }
.rll-hud { position: absolute; left: 0; right: 0; top: 0; height: 52px; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; box-sizing: border-box; }
.rll-hud > span { display: flex; align-items: center; gap: 10px; font: 700 15px "Chakra Petch", sans-serif; }
.rll-hud em { font-style: normal; color: #8ea3bd; font-weight: 600; }
.rll-hud i { font-style: normal; font-size: 12px; padding: 4px 10px; border-radius: 999px; background: rgba(25,240,176,.14); border: 1px solid rgba(25,240,176,.4); color: #19f0b0; }
.rll-hud b { width: 38px; height: 38px; border-radius: 10px; background: #18233b; border: 1px solid rgba(255,255,255,.14); display: block; }
.rll-board { position: absolute; left: 20px; right: 20px; top: 56px; bottom: 92px; border-radius: 999px; border: 2px solid rgba(255,210,63,.5); background: #04080f; overflow: hidden; }
.rll-board.rect { border-radius: 10px; }
.rll-board svg { width: 100%; height: 100%; display: block; opacity: .85; }
.rll-score { position: absolute; left: 20px; right: 20px; bottom: 46px; height: 38px; display: flex; gap: 4px; }
.rll-score span { flex: 1; min-width: 0; border-radius: 6px; background: #0d1526; border-left: 4px solid var(--c); display: flex; align-items: center; gap: 4px; padding: 0 4px; font-size: 10px; overflow: hidden; white-space: nowrap; }
.rll-score img { width: 14px; height: 14px; border-radius: 50%; }
.rll-steps { position: fixed; z-index: 100; left: 50%; bottom: 6px; transform: translateX(-50%); display: flex; gap: 3px; padding: 4px 8px; border-radius: 999px; background: rgba(4,7,14,.92); border: 1px dashed rgba(142,163,189,.4); font: 600 12px ui-monospace, Menlo, monospace; white-space: nowrap; max-width: 98vw; overflow-x: auto; }
.rll-steps button { height: 26px; padding: 0 9px; border: 0; border-radius: 999px; background: none; color: #8ea3bd; font: inherit; cursor: pointer; }
.rll-steps button:hover { color: #fff; background: rgba(255,255,255,.08); }
.rll-steps button.on { background: #ffd23f; color: #1d1402; }
.rll-log { position: fixed; z-index: 100; left: 8px; bottom: 44px; font: 11px ui-monospace, Menlo, monospace; color: #61718b; }
@media (max-height: 500px) and (orientation: landscape) {
  .rll-hud { height: 44px; padding: 0 44px; }
  .rll-hud > span { font-size: 12px; gap: 6px; }
  .rll-hud b { width: 32px; height: 32px; }
  .rll-board { left: 44px; right: 44px; top: 46px; bottom: 64px; }
  .rll-score { left: 44px; right: 44px; bottom: 34px; height: 26px; }
  .rll-score span b { display: none; }
  .rll-steps { bottom: 2px; padding: 2px 6px; font-size: 10px; }
  .rll-steps button { height: 20px; padding: 0 5px; }
  .rll-steps button:not(.on) em { display: none; }
  .rll-log { display: none; }
}
`;

export default function RoyaleLayerLab() {
  React.useEffect(() => { demoMatchProgress(); }, []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(STEPS.length - 1, (Number(params.get("s")) || 1) - 1)));
  const [nonce, setNonce] = useState(0);
  const [log, setLog] = useState("");
  const step = STEPS[idx];
  const firstSkip = useRef(params.get("t"));

  // the fake game: only what the layer reads (who has crashed, in order)
  const game = useRef<{ gameEvents: any[] }>({ gameEvents: [] });
  const pauseAt = useMemo(() => {
    const skip = firstSkip.current != null ? Number(firstSkip.current) : step.skipMs || 0;
    firstSkip.current = null;
    return Date.now() - skip;
  }, [idx, nonce]);
  useEffect(() => {
    game.current = { gameEvents: [] };
    const timers = (step.crashes || []).map(([ms, id]) => setTimeout(() => game.current.gameEvents.push({ type: "player_died", time: ms / 1000, playerId: id }), ms));
    return () => timers.forEach(clearTimeout);
  }, [idx, nonce]);

  const go = (i: number) => { setIdx((i + STEPS.length) % STEPS.length); setNonce((n) => n + 1); setLog(""); };
  const seatIds = step.seatIds || ["me"];
  const youIds = step.youIds || seatIds.filter((id) => step.players.some((p) => p.id === id));

  return (
    <div className="rll">
      <style>{css}</style>
      <div className="rll-hud">
        <span>Round {step.setup.royaleRound} <em>{step.setup.alive} alive</em> <i>{step.setup.mode}</i></span>
        <span><b /><b /><b /><b /></span>
      </div>
      <div className={`rll-board ${step.setup.shape === "Rectangle" ? "rect" : ""}`}>
        <svg viewBox="0 0 800 400" preserveAspectRatio="none" aria-hidden="true">
          {step.players.map((p, i) => {
            const x = 60 + ((i * 131) % 680), y = 50 + ((i * 89) % 300), d = i % 2 ? 1 : -1;
            return <path key={p.id} d={`M${x} ${y}c${40 * d} ${-30} ${70 * d} ${40} ${110 * d} ${10}s${30 * d} ${-60} ${80 * d} ${-20}`} fill="none" stroke={(wormColors as any)[p.colorId]} strokeWidth="3" strokeLinecap="round" />;
          })}
        </svg>
      </div>
      <div className="rll-score">
        {step.players.slice(0, 16).map((p) => (
          <span key={p.id} style={{ "--c": (wormColors as any)[p.colorId] } as React.CSSProperties}><img src={flagUrlSquare(p.flag || "xx")} alt="" /><b>{p.name}</b></span>
        ))}
      </div>

      <RoyaleLayer
        key={`${idx}-${nonce}`}
        kind="royale"
        phase={step.phase}
        setup={step.setup}
        next={step.phase === "countdown" ? step.next || null : null}
        result={step.result || null}
        royaleResult={step.royaleResult || null}
        players={step.players}
        playerById={BY_ID}
        score={step.score || {}}
        roundWinnerId={step.roundWinnerId || null}
        seatIds={seatIds}
        youIds={youIds}
        pending={false}
        myPlace={step.myPlace ?? null}
        pauseStartedAt={pauseAt}
        pauseMs={step.pauseMs || 0}
        moved={false}
        touch={false}
        getGameState={() => game.current}
        onLeave={() => setLog("onLeave")}
        onAgain={() => setLog("onAgain (Next Royale)")}
        onPlayNow={() => setLog("onPlayNow")}
        reward={step.reward ? <RoyaleReward saved={0} demo={step.reward} /> : undefined}
      />

      {log && <div className="rll-log">{log}</div>}
      <div className="rll-steps">
        {STEPS.map((st, i) => <button key={st.name} className={i === idx ? "on" : ""} onClick={() => go(i)}>{i + 1} <em style={{ fontStyle: "normal" }}>{st.name}</em></button>)}
        <button onClick={() => go(idx)}>Replay</button>
      </div>
    </div>
  );
}
